import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Paper
} from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import { useContent } from '../contexts/ContentContext';

const Contact = () => {
  const { language } = useLanguage();
  const { content } = useContent();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState(null);

  const t = content[language] || content.ar;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Error:', error);
      setStatus('error');
    }
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 200px)' }}>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
            {t.contactTitle}
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9 }}>
            {t.contactSubtitle}
          </Typography>
        </Container>
      </Box>

      {/* Contact Content */}
      <Box sx={{ py: 6, bgcolor: '#f9f9f9' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="stretch">
            {/* Contact Info - Left Side */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', pr: { md: 4 } }}>
                <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', mb: 3 }}>
                  {language === 'ar' ? 'تواصل معنا' : 'Get In Touch'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
                  {language === 'ar' 
                    ? 'نحن هنا للإجابة على أسئلتك وتقديم أفضل الخدمات. لا تتردد في التواصل معنا في أي وقت.'
                    : 'We are here to answer your questions and provide the best services. Feel free to contact us anytime.'}
                </Typography>

                {/* Contact Info Cards */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Owner Info */}
                  <Paper sx={{ p: 3, bgcolor: 'white', boxShadow: 1, borderRadius: 2, borderLeft: 4, borderLeftColor: 'primary.main' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                      👤 {language === 'ar' ? 'المالك' : 'Owner'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.owner}
                    </Typography>
                  </Paper>

                  {/* Location Info */}
                  <Paper sx={{ p: 3, bgcolor: 'white', boxShadow: 1, borderRadius: 2, borderLeft: 4, borderLeftColor: 'primary.main' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                      📍 {language === 'ar' ? 'الموقع' : 'Location'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.location}
                    </Typography>
                  </Paper>

                  {/* Phone Info */}
                  <Paper sx={{ p: 3, bgcolor: 'white', boxShadow: 1, borderRadius: 2, borderLeft: 4, borderLeftColor: 'primary.main' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                      📞 {language === 'ar' ? 'الهاتف' : 'Phone'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      +966 XX XXX XXXX
                    </Typography>
                  </Paper>

                  {/* Email Info */}
                  <Paper sx={{ p: 3, bgcolor: 'white', boxShadow: 1, borderRadius: 2, borderLeft: 4, borderLeftColor: 'primary.main' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                      📧 {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      info@alrasheed.com
                    </Typography>
                  </Paper>

                  {/* Rating Info */}
                  <Paper sx={{ p: 3, bgcolor: 'primary.main', boxShadow: 1, borderRadius: 2, color: 'white' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      ⭐ {language === 'ar' ? 'التقييم' : 'Rating'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      4.5 {language === 'ar' ? 'نجوم' : 'Stars'}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            </Grid>

            {/* Contact Form - Right Side */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', boxShadow: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', mb: 3 }}>
                    {language === 'ar' ? 'أرسل لنا رسالة' : 'Send us a Message'}
                  </Typography>
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t.contactForm.name}
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          size="small"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t.contactForm.email}
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          size="small"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t.contactForm.phone}
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          size="small"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t.contactForm.message}
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          multiline
                          rows={4}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                        />
                      </Grid>
                      {status === 'success' && (
                        <Grid item xs={12}>
                          <Alert severity="success" sx={{ borderRadius: 1 }}>
                            {language === 'ar' ? 'تم إرسال الرسالة بنجاح!' : 'Message sent successfully!'}
                          </Alert>
                        </Grid>
                      )}
                      {status === 'error' && (
                        <Grid item xs={12}>
                          <Alert severity="error" sx={{ borderRadius: 1 }}>
                            {language === 'ar' ? 'حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى.' : 'An error occurred. Please try again.'}
                          </Alert>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={status === 'sending'}
                          sx={{ py: 1.5, fontWeight: 700, borderRadius: 1 }}
                        >
                          {status === 'sending'
                            ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...')
                            : t.contactForm.submit}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Contact;
